<script setup lang="ts">
  import type { Article } from '../../types'

  export interface ArticleGroupSliderProps {
    articles: Article[]
    title?: string
    showBorder?: boolean
    iconName?: string
  }

  const props = withDefaults(defineProps<ArticleGroupSliderProps>(), {
    title: 'Related Stories',
    showBorder: false,
    iconName: 'related-stories',
  })

  defineEmits<{
    (e: 'article-click', article: Article): void
  }>()

  const isHovered = ref(false)

  const groupedArticles = computed(() => {
    const grouped = []
    for (let i = 0; i < props.articles.length; i += 3) {
      grouped.push(props.articles.slice(i, i + 3))
    }

    return grouped
  })
</script>
<template>
  <div
    class="rounded-8px shadow-article-card bg-background-default w-[300px] h-[304px]"
    :class="{
      'border-8px border border-border-neutral-subtle': showBorder,
    }"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div class="flex flex-row gap-8px px-16px pt-16px pb-8px items-center">
      <img
        v-if="iconName"
        :src="`/icons/${iconName}.svg`"
        alt="fire icon"
        class="h-16px w-16px dark:filter dark:invert"
      />
      <Typography
        color="text-content-primary"
        weight="font-semibold"
        line-height="leading-[20px]"
      >
        {{ title }}
      </Typography>
    </div>
    <UCarousel
      :items="groupedArticles"
      class="rounded-lg overflow-hidden"
      arrows
      indicators
      :ui="{
        wrapper: 'px-8px',
        item: 'basis-full',
        indicators: {
          wrapper: 'static py-8px my-4px gap-[8px]',
        },
      }"
    >
      <template #default="{ item, index }">
        <div class="flex flex-col">
          <ContentCard
            v-for="(article, row) in item"
            :key="article.articleId"
            :tile-position="index * 3 + row + 1"
            design="msn"
            variant="default"
            size="tiny"
            tile-type="article-group"
            v-bind="article"
            draggable="false"
            @click="$emit('article-click', article)"
          />
        </div>
      </template>
      <template #prev="{ onClick, disabled }">
        <ContentArrow
          class="top-[84px]"
          direction="left"
          :on-click="onClick"
          :disabled="disabled"
          :hidden="!isHovered"
        />
      </template>
      <template #next="{ onClick, disabled }">
        <ContentArrow
          class="top-[84px]"
          direction="right"
          :on-click="onClick"
          :disabled="disabled"
          :hidden="!isHovered"
        />
      </template>
      <template #indicator="{ onClick, page, active }">
        <SliderDots :on-click="onClick" :page="page" :active="active" />
      </template>
    </UCarousel>
  </div>
</template>
